import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Container, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import SmartImage from '../components/SmartImage';

const ContainerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [containerData, setContainerData] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [containerTypes, setContainerTypes] = useState([]);

  // UOM state
  const [uoms, setUoms] = useState([]);
  const [selectedUomId, setSelectedUomId] = useState('');
  const [uomResolving, setUomResolving] = useState(false);

  // Cache: uomId -> Set(containerTypeId)
  const uomToContainersRef = useRef(new Map());

  const { isAuthenticated, verifySession, logout } = useAuth();

  // --- Helpers ---------------------------------------------------------------

  const fetchContainerById = async (containerId) => {
    const res = await axios.get(`/container-types/${containerId}`, { withCredentials: true });
    return res.data?.data;
  };

  const ensureUomsLoaded = async () => {
    if (uoms.length) return uoms;
    const res = await axios.get('/uoms', { withCredentials: true });
    const list = res.data?.data || res.data || [];
    const asList = Array.isArray(list) ? list : [];
    setUoms(asList);
    return asList;
  };

  const fetchContainersForUom = async (uomId) => {
    if (uomToContainersRef.current.has(uomId)) {
      return uomToContainersRef.current.get(uomId);
    }
    const res = await axios.get(`/container-types/uom/${uomId}`, { withCredentials: true });
    const list = res.data?.data || res.data || [];
    const ids = new Set((Array.isArray(list) ? list : []).map(ct => ct.id));
    uomToContainersRef.current.set(uomId, ids);
    return ids;
  };

  const resolveUomForContainer = async (containerTypeId) => {
    if (!containerTypeId) {
      setSelectedUomId('');
      return;
    }
    setUomResolving(true);
    try {
      const uomList = await ensureUomsLoaded();

      // If the container already exposes uomId, use it directly
      if (editableData?.uomId) {
        const exists = uomList.some(u => u.id === editableData.uomId);
        if (exists) {
          setSelectedUomId(editableData.uomId);
          return;
        }
      }

      // Otherwise iterate UOMs and see whose container list contains this container
      for (const u of uomList) {
        try {
          const ids = await fetchcontainersForUomSafe(u.id);
          if (ids.has(containerTypeId)) {
            setSelectedUomId(u.id);
            return;
          }
        } catch {
          /* continue */
        }
      }
      setSelectedUomId('');
    } finally {
      setUomResolving(false);
    }
  };

  // small wrapper to keep logs quieter
  const fetchcontainersForUomSafe = async (uomId) => {
    try { return await fetchContainersForUom(uomId); }
    catch (e) { console.warn('UOM lookup failed for', uomId, e); return new Set(); }
  };

  const loadAll = async (initialId) => {
    try {
      setLoading(true);
      setError('');
      await verifySession();

      // Persisted container
      const persisted = await fetchContainerById(initialId);
      if (!persisted) {
        setError('Persisted container type not found');
        return;
      }
      setContainerData(persisted);
      setEditableData(persisted);

      // All container types
      const allTypesRes = await axios.get('/container-types', { withCredentials: true });
      let allTypes = allTypesRes.data?.data || [];
      if (!allTypes.some(ct => ct.id === persisted.id)) {
        allTypes.unshift(persisted);
      }
      setContainerTypes(allTypes);

      // Load UOMs and resolve current UOM for this container
      await ensureUomsLoaded();
      if (persisted.uomId) {
        setSelectedUomId(persisted.uomId);
      } else {
        await resolveUomForContainer(persisted.id);
      }
    } catch (err) {
      console.error('Error loading container types:', err);
      if (err.response?.status === 401) logout();
      else setError(err.response?.data?.message || 'Failed to load container types');
    } finally {
      setLoading(false);
    }
  };

  // --- Effects ---------------------------------------------------------------

  useEffect(() => {
    if (isAuthenticated && id) {
      loadAll(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, id]);

  // If the selected container type changes from the dropdown, reload details + UOM
  const handleContainerTypeChange = async (newContainerId) => {
    try {
      setLoading(true);
      setError('');
      const fresh = await fetchContainerById(newContainerId);
      if (fresh) {
        setContainerData(fresh);
        setEditableData(fresh);
        // Resolve UOM for the newly selected container
        if (fresh.uomId) setSelectedUomId(fresh.uomId);
        else await resolveUomForContainer(fresh.id);
      } else {
        setError('Selected container type could not be loaded');
      }
    } catch (e) {
      console.error('Error loading selected container:', e);
      if (e.response?.status === 401) logout();
      else setError(e.response?.data?.message || 'Failed to load selected container');
    } finally {
      setLoading(false);
    }
  };

  // --- Handlers --------------------------------------------------------------

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'id') {
      handleContainerTypeChange(value);
      return;
    }

    if (name === 'uomId') {
      // When switching to 'each', clear measure fields so nothing stale persists
      const sel = uoms.find(u => u.id === value);
      const nextLabel = (sel?.label || '').trim().toLowerCase();
      const nextDesc  = (sel?.description || '').trim().toLowerCase();
      const nextIsEach =
        `${nextLabel} - ${nextDesc}` === 'each - a single, individual item' ||
        nextLabel === 'each' ||
        nextLabel.startsWith('each ');

      setSelectedUomId(value);
      setEditableData(prev => ({
        ...prev,
        uomId: value,
        measureMax: nextIsEach ? '' : prev.measureMax,
        measureIncrement: nextIsEach ? '' : prev.measureIncrement,
      }));
      return;
    }

    const newValue = type === 'checkbox' ? checked : value;
    setEditableData((prev) => ({ ...prev, [name]: newValue }));
    setValidationErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Compute if the chosen UOM is "each"
  const isEachUomSelected = (() => {
    const sel = uoms.find(u => u.id === selectedUomId);
    if (!sel) return false;
    const label = (sel.label || '').trim().toLowerCase();
    const desc  = (sel.description || '').trim().toLowerCase();
    return (
      `${label} - ${desc}` === 'each - a single, individual item' ||
      label === 'each' ||
      label.startsWith('each ')
    );
  })();

  const handleUpdate = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    const errors = {};
    for (let level = 1; level <= 3; level++) {
      const enabled = editableData[`lowLevelWarning${level}Enable`];
      const value = editableData[`lowLevel${level}Warning`];
      if (enabled && (value === '' || value === null || !Number.isFinite(Number(value)))) {
        errors[`lowLevel${level}Warning`] = `Level ${level} warning value must be a valid number and not empty.`;
      }
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setLoading(false);
      return;
    }

    try {
      await verifySession();

      // Build payload; omit measure fields when UOM is 'each'
      const payload = {
        containerTypeName: editableData.containerTypeName,
        friendlyName: editableData.friendlyName,
        imageURL: editableData.imageURL,
        uomId: selectedUomId || null,
        lowLevelWarning1Enable: editableData.lowLevelWarning1Enable,
        lowLevelWarning2Enable: editableData.lowLevelWarning2Enable,
        lowLevelWarning3Enable: editableData.lowLevelWarning3Enable,
        lowLevel1Warning: Number(editableData.lowLevel1Warning),
        lowLevel2Warning: Number(editableData.lowLevel2Warning),
        lowLevel3Warning: Number(editableData.lowLevel3Warning),
      };

      if (!isEachUomSelected) {
        if (editableData.measureMax !== '' && editableData.measureMax !== undefined) {
          payload.measureMax = Number(editableData.measureMax);
        }
        if (editableData.measureIncrement !== '' && editableData.measureIncrement !== undefined) {
          payload.measureIncrement = Number(editableData.measureIncrement);
        }
      }

      await axios.put(
        `/container-types/${editableData.id}`,
        payload,
        { withCredentials: true }
      );

      setSuccess('Container updated successfully!');
    } catch (err) {
      console.error('Error updating container:', err);
      if (err.response?.status === 401) logout();
      else setError(err.response?.data?.message || 'Failed to update container');
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/dashboard/container-types');
  };

  // --- Render ----------------------------------------------------------------

  if (!isAuthenticated) return null;

  if (loading && !containerData) {
    return (
      <div className="content-container text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (!containerData) {
    return (
      <div className="content-container text-center py-5">
        <Alert variant="danger">Container not found</Alert>
        <Button variant="secondary" onClick={handleBackClick}>
          Back to Containers
        </Button>
      </div>
    );
  }

  const containerTitle = containerData.friendlyName || containerData.containerTypeName;

  return (
    <div className="content-container">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {success && <Alert variant="success" className="mb-3">{success}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button variant="outline-secondary" onClick={handleBackClick} disabled={loading}>
          ← Back to Containers
        </Button>
        <h1 className="mx-auto">{containerTitle}</h1>
        <Button variant="primary" onClick={handleUpdate} disabled={loading}>
          {loading ? (
            <>
              <Spinner as="span" size="sm" animation="border" role="status" />
              <span className="ms-2">Updating...</span>
            </>
          ) : 'Update Container'}
        </Button>
      </div>

      <Container className="text-center my-4">
        <SmartImage
          src={editableData.imageURL || '/placeholder-container.png'}
          alt={containerTitle}
          placeholder="/placeholder-product.png"
          className="img-fluid"
          style={{ maxHeight: '300px' }}
          onError={(e) => (e.target.src = '/placeholder-container.png')}
        />
      </Container>

      <Form className="mt-4">
        {/* Switch to a different container record */}
        <Form.Group className="mb-3">
          <Form.Label>Container Type</Form.Label>
          <Form.Select
            name="id"
            value={editableData.id || ''}
            onChange={handleInputChange}
            disabled={loading}
          >
            <option value="">Select Container Type</option>
            {containerTypes.map((ct) => (
              <option key={ct.id} value={ct.id}>
                {ct.friendlyName || ct.containerTypeName}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* UOM selector */}
        <Form.Group className="mb-3">
          <Form.Label>Container UOM</Form.Label>
          <Form.Select
            name="uomId"
            value={selectedUomId || ''}
            onChange={handleInputChange}
            disabled={loading || uomResolving || uoms.length === 0}
          >
            <option value="">
              {uomResolving ? 'Resolving UOM…' : (uoms.length ? 'Select UOM' : 'No UOMs available')}
            </option>
            {uoms.map(u => (
              <option key={u.id} value={u.id}>
                {u.label}{u.description ? ` - ${u.description}` : ''}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Choose the unit of measure associated with this container.
            {isEachUomSelected ? ' (Note: “each” hides measurement fields.)' : ''}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Friendly Name</Form.Label>
          <Form.Control
            type="text"
            name="friendlyName"
            value={editableData.friendlyName || ''}
            onChange={handleInputChange}
            disabled={loading}
          />
        </Form.Group>

        {/* Show measure fields ONLY when UOM is not "each" */}
        {!isEachUomSelected && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Measure Max</Form.Label>
              <Form.Control
                type="number"
                name="measureMax"
                value={editableData.measureMax || ''}
                onChange={handleInputChange}
                disabled={loading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Measure Increment</Form.Label>
              <Form.Control
                type="number"
                name="measureIncrement"
                value={editableData.measureIncrement || ''}
                onChange={handleInputChange}
                disabled={loading}
              />
            </Form.Group>
          </>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Image URL</Form.Label>
          <Form.Control
            type="text"
            name="imageURL"
            value={editableData.imageURL || ''}
            onChange={handleInputChange}
            disabled={loading}
          />
        </Form.Group>

        {[1, 2, 3].map((level) => (
          <React.Fragment key={level}>
            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                name={`lowLevelWarning${level}Enable`}
                checked={editableData[`lowLevelWarning${level}Enable`] || false}
                onChange={handleInputChange}
                label={`Enable Level ${level} Warning`}
                disabled={loading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Level {level} Warning Value</Form.Label>
              <Form.Control
                type="number"
                name={`lowLevel${level}Warning`}
                value={editableData[`lowLevel${level}Warning`] || ''}
                onChange={handleInputChange}
                disabled={loading}
                isInvalid={!!validationErrors[`lowLevel${level}Warning`]}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors[`lowLevel${level}Warning`]}
              </Form.Control.Feedback>
            </Form.Group>
          </React.Fragment>
        ))}
      </Form>
    </div>
  );
};

export default ContainerDetail;